import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { i18n } from '@lingui/core'
import { isNil } from 'ramda'
import styled, { useTheme } from 'styled-components'

import { globalDefinitions } from '@/defined-translations'
import { formatListDate, formatLocale } from '@/modules/shared/helpers/locale'
import { type Task, TaskStatus } from '@/modules/task/types/task.types'
import { useTaskStatusMetaData } from '../../hooks/useTaskStatusMetaData'

type TaskStatusLabelProps = {
  task: Task
  display?: string
  /**
   * 'list' shows type-specific copy for completed tasks
   * 'detail' shows check icon + green color + just the date
   */
  variant?: 'list' | 'detail'
}

export const TaskStatusLabel = ({ task, display: displayProp, variant = 'list' }: TaskStatusLabelProps) => {
  const theme = useTheme()
  const { color, status, daysTillExpiry } = useTaskStatusMetaData(task)

  const getCompletedDisplayForList = () => {
    const date = formatLocale(task.completedAt ?? task.updatedAt, 'dd MMMM')

    switch (task.type) {
      case 'Message': {
        return i18n._(globalDefinitions.task.Message.completed_list, { date })
      }
      case 'Document': {
        return i18n._(globalDefinitions.task.Document.completed_list, { date })
      }
      case 'Questionnaire': {
        return i18n._(globalDefinitions.task.Questionnaire.completed_list, {
          date,
        })
      }
      default: {
        return formatLocale(task.completedAt ?? task.updatedAt, 'dd MMMM')
      }
    }
  }

  const getDisplay = () => {
    if (displayProp) {
      return displayProp
    }

    switch (status) {
      case TaskStatus.Expired: {
        if (task.type === 'Hylyght' && task.activity?.type === 'program') {
          return i18n._(globalDefinitions.task.Hylyght.expired_program, {
            date: formatLocale(task.activity?.enddate, 'dd MMMM'),
          })
        }

        return i18n._(globalDefinitions.task.expired)
      }

      case TaskStatus.Completed: {
        // Detail variant: just show the date (check icon + green handled separately)
        if (variant === 'detail') {
          return formatLocale(task.completedAt ?? task.updatedAt, 'dd MMMM yyyy')
        }

        // List variant: show type-specific copy
        return getCompletedDisplayForList()
      }

      case TaskStatus.Pending: {
        if (task.repeating) {
          return i18n._(globalDefinitions.task.repeating)
        }

        if (task.recurring) {
          return i18n._(globalDefinitions.task.recurring)
        }

        if (!isNil(daysTillExpiry)) {
          if (daysTillExpiry === 0) {
            return i18n._(globalDefinitions.task.expires_today)
          }

          if (daysTillExpiry === 1) {
            return i18n._(globalDefinitions.task.expires_in_one_day)
          }

          return i18n._(globalDefinitions.task.expires_in_x_days, {
            count: daysTillExpiry,
          })
        }

        // The list rows use the short date of the redesign; the detail screen keeps the full one.
        return i18n._(globalDefinitions.task.no_expiry, {
          date: variant === 'list' ? formatListDate(task.createdAt) : formatLocale(task.createdAt, 'dd/MM/yyyy'),
        })
      }
    }
  }

  // For detail variant + completed: use green color
  const displayColor = variant === 'detail' && status === TaskStatus.Completed ? theme.Colors.Status.Success : color

  return (
    <StTaskStatus color={displayColor}>
      {/* Check icon for completed tasks on detail screen */}
      {variant === 'detail' && status === TaskStatus.Completed ? (
        <StCheckIconCircle>
          <FontAwesomeIcon icon={['fal', 'check']} fontSize={theme.UI.IconSize.XSmall} />
        </StCheckIconCircle>
      ) : null}

      {/* Repeat icon for recurring/repeating tasks */}
      {task.status === TaskStatus.Pending && (task.recurring || task.repeating) ? (
        <FontAwesomeIcon icon={['fal', 'arrows-repeat']} />
      ) : null}

      {getDisplay()}
    </StTaskStatus>
  )
}

const StTaskStatus = styled.span<{ color: string }>`
  font-size: ${({ theme }) => theme.Typography.BodySmallRegular['font-size']};
  line-height: ${({ theme }) => theme.Typography.BodySmallRegular['line-height']};

  color: ${(props) => props.color};

  margin: 0;
  padding: 0;
  white-space: nowrap;

  flex-shrink: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space2};
`

const StCheckIconCircle = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.Colors.Status.Success}33;
  width: ${({ theme }) => theme.UI.SpacingPx.Space5};
  height: ${({ theme }) => theme.UI.SpacingPx.Space5};
  border-radius: 50%;
`
